import { v4 as uuidv4 } from "uuid";
import {
  Assessment,
  Stack,
  BusinessFunction,
  Tool,
  StackType,
  CMMIRating,
} from "../types";
import { STACK_TEMPLATES } from "../constants/stacks";

/**
 * Create a new empty tool
 */
export const createTool = (name: string = "New Tool"): Tool => ({
  id: uuidv4(),
  name,
  currentRating: 2 as CMMIRating, // Default to Proactive
  status: "Active",
});

/**
 * Create a new empty function
 */
export const createFunction = (
  name: string = "New Function",
): BusinessFunction => ({
  id: uuidv4(),
  name,
  tools: [],
});

/**
 * Create a new stack with default functions
 */
export const createStack = (stackType: StackType): Stack => {
  const template = STACK_TEMPLATES.find((t) => t.name === stackType);
  const functions = (template?.defaultFunctions || []).map((name) =>
    createFunction(name),
  );

  return {
    id: uuidv4(),
    name: stackType,
    functions,
  };
};

/**
 * Create a new empty assessment with all 5 stacks
 */
export const createAssessment = (
  title: string = "New Assessment",
): Assessment => {
  const stacks = STACK_TEMPLATES.map((template) => createStack(template.name));

  return {
    id: uuidv4(),
    title,
    appVersion: "2.0.0",
    fileVersion: "2.0",
    lastEdited: new Date().toISOString(),
    stacks,
    metadata: {
      date: new Date().toISOString().split("T")[0],
    },
  };
};

/**
 * Create a sample assessment with demo data
 */
export const createSampleAssessment = (): Assessment => {
  const assessment = createAssessment("Sample Company Assessment");

  // Add some sample tools to demonstrate the system
  const salesStack = assessment.stacks.find((s) => s.name === "Sales");
  if (salesStack && salesStack.functions.length > 0) {
    salesStack.functions[0].tools = [
      {
        id: uuidv4(),
        name: "HubSpot CRM",
        currentRating: 3,
        status: "Active",
        notes: "Primary CRM system",
      },
      {
        id: uuidv4(),
        name: "LinkedIn Sales Navigator",
        currentRating: 2,
        status: "Active",
      },
      {
        id: uuidv4(),
        name: "AI Lead Scoring",
        currentRating: 0,
        proposedTool: "Salesforce Einstein",
        proposedRating: 4,
        status: "Proposed",
        agenticScore: {
          automatability: 4,
          impact: 4,
          frequency: 4,
          dataReadiness: 3,
          riskProfile: 3,
        },
      },
    ];
  }

  const financeStack = assessment.stacks.find((s) => s.name === "Finance");
  if (financeStack && financeStack.functions.length > 0) {
    financeStack.functions[0].tools = [
      {
        id: uuidv4(),
        name: "Xero",
        currentRating: 4,
        status: "Active",
        notes: "Cloud accounting",
      },
      {
        id: uuidv4(),
        name: "Manual Invoicing",
        currentRating: 1,
        status: "Active",
      },
    ];
  }

  return assessment;
};
